import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  Loader2, UploadCloud, FileSpreadsheet, Trash2, ShieldCheck, TrendingUp, Check, AlertCircle, Wallet, X, Gift, HelpCircle,
} from 'lucide-react';
import AppShell from '@/components/AppShell';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('kylo_token')}` });

const CUR = { GBP: '£', USD: '$', EUR: '€', CAD: 'C$', AUD: 'A$' };
const money = (n, cur = 'GBP') => `${CUR[cur] || ''}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TOOLTIP_STYLE = {
  backgroundColor: '#FFFDF9', border: '1px solid #E6DED1', borderRadius: 10,
  color: '#1F1B16', fontSize: 12, boxShadow: '0 8px 24px rgba(70,45,20,0.10)',
};
const BAR_COLORS = ['#9D174D', '#C2410C', '#B45309', '#15803D', '#1D4ED8', '#7C3AED'];

const SAMPLE = {
  primary_currency: 'GBP',
  running_total: 1284.5,
  total_income: 1402.0,
  total_expense: 117.5,
  transaction_count: 212,
  monthly: [
    { month: '2025-07', label: 'Jul 2025', net: 210.4, income: 230 },
    { month: '2025-08', label: 'Aug 2025', net: 318.9, income: 340 },
    { month: '2025-09', label: 'Sep 2025', net: 402.1, income: 430 },
    { month: '2025-10', label: 'Oct 2025', net: 353.1, income: 402 },
  ],
  daily: Array.from({ length: 28 }, (_, i) => ({
    date: `2025-10-${String(i + 1).padStart(2, '0')}`,
    net: Math.round((8 + Math.sin(i / 2) * 6 + (i % 5) * 3) * 100) / 100,
  })),
  by_type: [
    { type: 'Affiliate commission', net: 980.2 },
    { type: 'Ads commission', net: 304.3 },
  ],
};

const fmtAxis = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v);
const dayLabel = (d) => { const p = String(d).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : d; };

const TaxHelp = ({ testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className="inline-flex items-center gap-1 text-[#9D174D] hover:underline font-medium" data-testid={testid}>
        <HelpCircle className="w-3.5 h-3.5" /> Where do I find my statements?
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 text-sm bg-[#FFFDF9] border-[#E6DED1]" data-testid={`${testid}-content`}>
      <p className="font-medium text-[#1F1B16] mb-1.5">Download your TikTok income statement</p>
      <p className="text-[#6F675D] leading-relaxed">In TikTok, go to:</p>
      <ol className="mt-1.5 space-y-1 text-[#3F3A33] list-decimal list-inside">
        <li>Creator Center</li>
        <li>Earnings</li>
        <li>The <span className="font-medium">More</span> button</li>
        <li>Tax Documents → Tax Statements</li>
        <li>Earnings → download the file</li>
      </ol>
      <p className="text-xs text-[#9A9188] mt-2">Upload that .xlsx/.csv file here.</p>
    </PopoverContent>
  </Popover>
);

const StatCard = ({ label, value, testid }) => (
  <div className="rounded-[14px] border border-[#E6DED1] bg-[#FFFDF9] p-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-wider text-[#8A8178]">{label}</p>
    <p className="font-display text-xl text-[#1F1B16] mt-1">{value}</p>
  </div>
);

const ChartShell = ({ title, children, full }) => (
  <div className={`rounded-[16px] border border-[#EFE1D1] bg-[#FBF7F0] p-4 ${full ? 'sm:col-span-2' : ''}`}>
    <p className="text-[11px] uppercase tracking-wider text-[#8A8178] mb-3">{title}</p>
    <div className="h-56 w-full min-w-0">{children}</div>
  </div>
);

export default function IncomeCalculatorPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/income/summary`, { headers: authHeaders() });
      setSummary(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const hasData = summary?.has_data;
  const view = hasData ? summary : SAMPLE;
  const cur = view.primary_currency || 'GBP';

  const typeData = useMemo(() => (view.by_type || []).map((t, i) => ({ ...t, fill: BAR_COLORS[i % BAR_COLORS.length] })), [view.by_type]);

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(`${API}/api/income/upload`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setResults(res.data.results || []);
      setSummary(res.data.summary);
      const ok = (res.data.results || []).filter((r) => r.recognised && r.inserted > 0).length;
      const dup = (res.data.results || []).filter((r) => r.is_duplicate_month).length;
      const bad = (res.data.results || []).filter((r) => !r.recognised).length;
      if (ok) toast.success(`Added ${ok} statement${ok > 1 ? 's' : ''} to your totals`);
      if (dup) toast.message('Already counted — duplicate month skipped');
      if (bad) toast.error(`${bad} file${bad > 1 ? 's' : ''} not recognised`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const deleteAll = async () => {
    try {
      await axios.delete(`${API}/api/income/all`, { headers: authHeaders() });
      setResults([]);
      await load();
      toast.success('All your income data has been deleted');
    } catch { toast.error('Could not delete your data.'); }
  };

  const deleteMonth = async (monthKey, label) => {
    try {
      const res = await axios.delete(`${API}/api/income/month/${monthKey}`, { headers: authHeaders() });
      setSummary(res.data.summary);
      setResults([]);
      toast.success(`Removed ${label}`);
    } catch { toast.error('Could not remove that month.'); }
  };

  return (
    <AppShell title="Income calculator">
      <SEO title="TikTok Income Calculator — Poettik" description="Upload your TikTok earnings exports and see your cumulative income." noindex />
      <div className="px-4 sm:px-8 py-6 max-w-5xl space-y-6" data-testid="income-calculator-page">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-[12px] bg-[#FBE4EE] text-[#9D174D] flex items-center justify-center shrink-0">
            <Wallet className="w-5 h-5" strokeWidth={1.8} />
          </span>
          <div>
            <h2 className="font-display text-xl text-[#1F1B16] leading-tight">Your TikTok income</h2>
            <p className="text-sm text-[#6F675D] mt-0.5">Upload your monthly TikTok earnings exports and watch your running total build up.</p>
            <p className="text-sm mt-1"><TaxHelp testid="tax-help-header" /></p>
          </div>
        </div>

        {/* Upload — compact */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); }}
          className={`rounded-[14px] border border-dashed px-4 py-3 flex items-center gap-3 transition-colors ${dragOver ? 'border-[#9D174D] bg-[#FBE4EE]/40' : 'border-[#E0D6C6] bg-[#FBF7F0]'}`}
          data-testid="income-dropzone"
        >
          <input
            ref={fileRef} type="file" accept=".xlsx,.xls,.csv" multiple className="hidden"
            onChange={(e) => doUpload(e.target.files)} data-testid="income-file-input"
          />
          <UploadCloud className="w-5 h-5 text-[#9D174D] shrink-0" strokeWidth={1.8} />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-[#1F1B16] leading-tight">Drop your TikTok export, or choose a file</p>
            <p className="text-[11px] text-[#9A9188] inline-flex items-center gap-1 mt-0.5">
              <ShieldCheck className="w-3 h-3 text-[#15803D]" /> No personal info stored · delete anytime · .xlsx/.csv
            </p>
            <p className="text-[11px] mt-1"><TaxHelp testid="tax-help-upload" /></p>
          </div>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" className="shrink-0" data-testid="income-upload-btn">
            {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Reading…</> : 'Choose files'}
          </Button>
        </div>

        {/* Upload results */}
        {results.length > 0 && (
          <div className="space-y-2" data-testid="income-upload-results">
            {results.map((r, i) => (
              <div key={i} className={`flex items-start gap-2.5 rounded-[12px] border p-3 text-sm ${r.recognised ? 'border-[#BBF7D0] bg-[#F0FDF4]' : 'border-[#FECACA] bg-[#FEF2F2]'}`}>
                {r.recognised ? <Check className="w-4 h-4 text-[#15803D] mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 text-[#B91C1C] mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium text-[#1F1B16] truncate">{r.filename}</p>
                  {r.recognised ? (
                    <p className="text-[#3F3A33]">
                      Detected: {r.report_type} — {r.month_label}, {r.count} transactions, {money(r.total, r.currency)}.
                      {r.is_duplicate_month ? ' Already counted (no duplicates added).' : ` Added ${r.inserted} new.`}
                    </p>
                  ) : (
                    <p className="text-[#B91C1C]">{r.reason}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#9D174D]" /></div>
        ) : (
          <div className="relative">
            {/* Sample overlay — kept light so the sample charts stay readable */}
            {!hasData && (
              <div className="absolute inset-x-0 top-0 z-10 flex justify-center pt-2" data-testid="income-sample-overlay">
                <div className="rounded-full bg-[#1F1B16] text-[#FFFDF9] text-xs sm:text-sm px-4 py-2 shadow-lg">
                  Sample data — upload your TikTok export to see your real figures
                </div>
              </div>
            )}

            <div className={`space-y-4 ${!hasData ? 'pointer-events-none' : ''}`}>
              {/* Stat cards */}
              <div className="grid grid-cols-3 gap-3" data-testid="income-stats">
                <StatCard testid="income-running-total" label="Running total" value={money(view.running_total, cur)} />
                <StatCard label="Total income" value={money(view.total_income, cur)} />
                <StatCard label="Transactions" value={(view.transaction_count || 0).toLocaleString('en-GB')} />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ChartShell title="Monthly earnings">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={view.monthly} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAE0D2" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" />
                      <YAxis tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" tickFormatter={fmtAxis} width={44} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [money(v, cur), 'Net']} />
                      <Bar dataKey="net" fill="#9D174D" radius={[5, 5, 0, 0]} maxBarSize={48} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartShell>

                <ChartShell title="By earnings type">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={typeData} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAE0D2" horizontal={false} />
                      <XAxis type="number" tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" tickFormatter={fmtAxis} />
                      <YAxis type="category" dataKey="type" tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" width={110} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [money(v, cur), 'Net']} />
                      <Bar dataKey="net" radius={[0, 5, 5, 0]} maxBarSize={34}>
                        {typeData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartShell>

                <ChartShell title="Daily income" full>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={view.daily} margin={{ top: 4, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EAE0D2" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" interval="preserveStartEnd" minTickGap={24} />
                      <YAxis tick={{ fill: '#8A8178', fontSize: 11 }} stroke="#D9CFC0" tickFormatter={fmtAxis} width={44} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} labelFormatter={dayLabel} formatter={(v) => [money(v, cur), 'Net']} />
                      <Line type="monotone" dataKey="net" stroke="#9D174D" strokeWidth={2.5} dot={{ r: 2, fill: '#9D174D' }} activeDot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartShell>
              </div>
            </div>
          </div>
        )}

        {/* Uploaded months + delete controls */}
        {hasData && (
          <div className="rounded-[16px] border border-[#E6DED1] bg-[#FFFDF9] p-4 space-y-3" data-testid="income-months">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[#1F1B16]">Uploaded months</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-[#B91C1C] border-[#FECACA] hover:bg-[#FEF2F2]" data-testid="income-delete-all">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete all
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="income-delete-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete all income data?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes every transaction you've uploaded. This can't be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteAll} className="bg-[#B91C1C] hover:bg-[#991B1B]" data-testid="income-delete-confirm">Delete everything</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div className="flex flex-wrap gap-2">
              {(summary.monthly || []).map((m) => (
                <div key={m.month} className="flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full border border-[#E6DED1] bg-[#FBF7F0]" data-testid={`income-month-${m.month}`}>
                  <span className="text-sm text-[#1F1B16]">{m.label}</span>
                  <span className="text-xs text-[#6F675D]">{money(m.net, cur)}</span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="w-6 h-6 rounded-full flex items-center justify-center text-[#B91C1C] hover:bg-[#FEE2E2] transition-colors" title={`Remove ${m.label}`} data-testid={`income-delete-month-${m.month}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remove {m.label}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This deletes just this month's transactions from your totals. You can re-upload it later.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteMonth(m.month, m.label)} className="bg-[#B91C1C] hover:bg-[#991B1B]" data-testid={`income-delete-month-confirm-${m.month}`}>Remove month</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Samples — coming soon (disabled) */}
        <div className="rounded-[16px] border border-dashed border-[#E0D6C6] bg-[#FBF7F0]/60 p-4 flex items-center justify-between gap-3" data-testid="income-samples-soon">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-[10px] bg-[#EFE7DB] text-[#9A9188] flex items-center justify-center shrink-0">
              <Gift className="w-4 h-4" strokeWidth={1.8} />
            </span>
            <div>
              <p className="text-sm font-medium text-[#6F675D]">Add product samples (name + value)</p>
              <p className="text-xs text-[#9A9188] mt-0.5">Fold the retail value of PR/sample products into your earnings.</p>
            </div>
          </div>
          <button
            disabled
            className="shrink-0 h-9 px-3 rounded-full border border-[#E0D6C6] text-xs font-medium text-[#9A9188] bg-[#F4ECE0] cursor-not-allowed"
            data-testid="income-samples-btn"
          >
            Coming soon
          </button>
        </div>
      </div>
    </AppShell>
  );
}
